/*
 * Julvärmen collection area (boards Collection / M-Collection). Used by jv-main-collection and jv-main-search.
 *
 * <jv-collection> loads chips, sort options and "Visa fler" in place: every one of them is a plain link, and this
 * element fetches the linked URL through the Section Rendering API, swaps the parts marked data-jv-region (chip
 * band, grid, sort sheet), refreshes the collection header (any section marked data-jv-sync-section), updates the
 * page title and pushes the URL so Back and Forward work. A chip that leads to another collection is loaded the
 * same way when that collection uses the same template; otherwise, or when a request fails, the browser simply
 * follows the link. Everything works without this file.
 *
 * On phones the "Sortera" button opens the bottom sheet (dialog.jv-dialog--sheet) instead of the dropdown, and the
 * chip rail scrolls the active chip into view.
 */
(() => {
  'use strict';

  if (customElements.get('jv-collection')) return;

  const phone = window.matchMedia('(max-width: 749px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const RENDER_PARAMS = ['section_id', 'sections'];
  const REGIONS = ['bar', 'results', 'sheet', 'summary'];
  const CACHE_LIMIT = 20;

  const api = () => window.JV || {};
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  const escapeSelector = (value) =>
    window.CSS && typeof window.CSS.escape === 'function' ? window.CSS.escape(value) : String(value).replace(/["\\]/g, '\\$&');
  const isPlainClick = (event) => event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

  /** The URL without Section Rendering parameters (they can leak into links rendered by those requests). */
  const cleanURL = (href) => {
    const url = new URL(href, window.location.origin);
    RENDER_PARAMS.forEach((name) => url.searchParams.delete(name));
    return url;
  };

  const sanitizeLinks = (root) => {
    root.querySelectorAll('a[href]').forEach((link) => {
      const href = link.getAttribute('href');
      if (!href || !/[?&](section_id|sections)=/.test(href)) return;
      const url = cleanURL(href);
      link.setAttribute('href', url.pathname + url.search + url.hash);
    });
  };

  const collectionPath = /\/collections\/[^/]+\/?$/;

  class JvCollection extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.cache = new Map();
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };

      this.addEventListener('click', (event) => this.onClick(event), options);
      document.addEventListener('click', (event) => this.onDocumentClick(event), options);
      document.addEventListener('keydown', (event) => this.onKeydown(event), options);
      window.addEventListener('popstate', (event) => this.onPopState(event), options);
      phone.addEventListener('change', () => this.onBreakpoint(), options);

      if (this.isPrimary && window.history.replaceState) {
        const state = window.history.state && typeof window.history.state === 'object' ? window.history.state : {};
        if (state.jvResults !== this.sectionId) {
          window.history.replaceState({ ...state, jvResults: this.sectionId }, '', window.location.href);
        }
      }
      window.requestAnimationFrame(() => this.centerActiveChip());
    }

    disconnectedCallback() {
      if (this.listeners) this.listeners.abort();
      if (this.request) this.request.abort();
    }

    get isPrimary() {
      return document.querySelector('jv-collection') === this;
    }

    get basePath() {
      return new URL(this.dataset.basePath || window.location.pathname, window.location.origin).pathname;
    }

    region(name, root = this) {
      return root.querySelector(`[data-jv-region="${name}"]`);
    }

    /** Links this element can load in place: its own page, or another collection when this is a collection page. */
    canLoad(url) {
      if (url.origin !== window.location.origin) return false;
      if (url.pathname === this.basePath) return true;
      return collectionPath.test(this.basePath) && collectionPath.test(url.pathname);
    }

    /* ---------------- events */

    onClick(event) {
      if (event.defaultPrevented || !(event.target instanceof Element)) return;
      const target = event.target;

      const toggle = target.closest('[data-jv-sort-toggle]');
      if (toggle) {
        const sheet = this.querySelector('[data-jv-sort-sheet]');
        const JV = api();
        if (phone.matches && sheet && typeof JV.openDialog === 'function') {
          event.preventDefault();
          JV.openDialog(sheet, toggle);
          const current = sheet.querySelector('.jv-sort__opt[aria-current]') || sheet.querySelector('.jv-sort__opt');
          if (current) window.setTimeout(() => current.focus({ preventScroll: true }), 60);
        }
        return;
      }

      const more = target.closest('a[data-jv-load-more]');
      if (more && isPlainClick(event)) {
        event.preventDefault();
        this.loadMore(more);
        return;
      }

      const link = target.closest('a[data-jv-nav]');
      if (!link || !isPlainClick(event)) return;
      const url = cleanURL(link.href);
      if (!this.canLoad(url)) return;
      event.preventDefault();

      const kind = link.dataset.jvNav;
      const dialog = link.closest('dialog');
      const JV = api();
      if (dialog && typeof JV.closeDialog === 'function') JV.closeDialog(dialog);
      const menu = link.closest('details[data-jv-sort]');
      if (menu) menu.open = false;

      let focus = { count: true };
      if (kind === 'chip') focus = { id: link.id };
      else if (kind === 'sort') focus = { sort: true };
      this.navigate(url, { push: true, focus, scroll: kind !== 'chip' });
    }

    onDocumentClick(event) {
      const menu = this.querySelector('details[data-jv-sort][open]');
      if (menu && event.target instanceof Node && !menu.contains(event.target)) menu.open = false;
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      const menu = this.querySelector('details[data-jv-sort][open]');
      if (!menu) return;
      menu.open = false;
      const summary = menu.querySelector('summary');
      if (summary) summary.focus();
    }

    onPopState(event) {
      const state = event.state;
      if (!state || state.jvResults !== this.sectionId || !this.isPrimary) return;
      const url = cleanURL(window.location.href);
      if (!this.canLoad(url)) {
        window.location.reload();
        return;
      }
      this.navigate(url, { push: false, scroll: false, focus: null });
    }

    onBreakpoint() {
      const JV = api();
      const sheet = this.querySelector('[data-jv-sort-sheet]');
      if (!phone.matches && sheet && sheet.open && typeof JV.closeDialog === 'function') JV.closeDialog(sheet);
      const menu = this.querySelector('details[data-jv-sort][open]');
      if (phone.matches && menu) menu.open = false;
      this.centerActiveChip();
    }

    /* ---------------- fetching and rendering */

    syncIds() {
      const ids = [...document.querySelectorAll('[data-jv-sync-section]')]
        .map((element) => element.dataset.jvSyncSection)
        .filter((id) => id && id !== this.sectionId);
      return [...new Set(ids)].slice(0, 4);
    }

    async fetchSections(path, signal) {
      const ids = [this.sectionId, ...this.syncIds()];
      const key = `${path}::${ids.join(',')}`;
      if (this.cache.has(key)) return this.cache.get(key);
      const url = new URL(path, window.location.origin);
      url.searchParams.set('sections', ids.join(','));
      const response = await fetch(url.toString(), { signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Loading failed (${response.status})`);
      const data = await response.json();
      if (!data || !data[this.sectionId]) throw new Error('Loading failed (no section)');
      this.cache.set(key, data);
      if (this.cache.size > CACHE_LIMIT) this.cache.delete(this.cache.keys().next().value);
      return data;
    }

    async navigate(url, options = {}) {
      url.searchParams.delete('page');
      const path = url.pathname + url.search;

      if (this.request) this.request.abort();
      const request = new AbortController();
      this.request = request;
      const busyTimer = window.setTimeout(() => this.setBusy(true), 150);

      try {
        const data = await this.fetchSections(path, request.signal);
        if (request.signal.aborted) return;
        this.render(data, options);
        if (options.push && window.history.pushState) {
          window.history.pushState({ jvResults: this.sectionId }, '', path);
        }
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        window.location.assign(path);
      } finally {
        window.clearTimeout(busyTimer);
        if (this.request === request) {
          this.request = null;
          this.setBusy(false);
        }
      }
    }

    setBusy(busy) {
      this.classList.toggle('is-loading', busy);
      const results = this.region('results');
      if (results) results.setAttribute('aria-busy', String(busy));
    }

    render(data, options) {
      const doc = parseHTML(data[this.sectionId]);
      sanitizeLinks(doc);
      const fresh = doc.querySelector(`jv-collection[data-section-id="${escapeSelector(this.sectionId)}"]`);
      if (!fresh) throw new Error('Loading failed (no results)');

      REGIONS.forEach((name) => this.swap(name, fresh));
      if (fresh.dataset.basePath) this.dataset.basePath = fresh.dataset.basePath;
      if (fresh.dataset.pageTitle) document.title = fresh.dataset.pageTitle;

      const JV = api();
      this.syncIds().forEach((id) => {
        if (!data[id]) return;
        const sectionDoc = parseHTML(data[id]);
        sanitizeLinks(sectionDoc);
        const next = sectionDoc.querySelector(`[data-jv-sync-section="${escapeSelector(id)}"]`);
        const current = document.querySelector(`[data-jv-sync-section="${escapeSelector(id)}"]`);
        if (!next || !current) return;
        const imported = document.importNode(next, true);
        current.replaceWith(imported);
        if (typeof JV.reveal === 'function') JV.reveal(imported);
      });

      if (typeof JV.reveal === 'function') JV.reveal(this);
      if (JV.wishlist && typeof JV.wishlist.sync === 'function') JV.wishlist.sync(this);
      window.requestAnimationFrame(() => this.centerActiveChip());

      this.restoreFocus(options.focus);
      const count = this.querySelector('[data-jv-count]');
      if (count && document.activeElement !== count) this.announce(count.textContent);
      if (options.scroll !== false) this.scrollToBar();
      this.dispatchEvent(new CustomEvent('jv:results:update', { bubbles: true, detail: { sectionId: this.sectionId } }));
    }

    swap(name, fresh) {
      const current = this.region(name);
      const next = this.region(name, fresh);
      if (current && next) current.replaceWith(document.importNode(next, true));
    }

    restoreFocus(focus) {
      if (!focus) return;
      let target = null;
      if (focus.id) target = document.getElementById(focus.id);
      else if (focus.sort) target = this.querySelector('[data-jv-sort-toggle]');
      if (!target && (focus.count || focus.id || focus.sort)) target = this.querySelector('[data-jv-count]');
      if (target) target.focus({ preventScroll: true });
    }

    announce(message) {
      const live = this.querySelector('[data-jv-live]');
      const text = String(message || '').replace(/\s+/g, ' ').trim();
      if (!live || !text) return;
      live.textContent = '';
      window.setTimeout(() => {
        live.textContent = text;
      }, 80);
    }

    scrollToBar() {
      const bar = this.region('bar') || this;
      if (bar.hidden || bar.getBoundingClientRect().top >= 0) return;
      bar.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
    }

    centerActiveChip() {
      const track = this.querySelector('.jv-chips [data-jv-rail-track]');
      if (!track || track.scrollWidth <= track.clientWidth + 1) return;
      const active = track.querySelector('[aria-current="page"]');
      if (!active) return;
      const item = active.closest('li') || active;
      const offset = item.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
      track.scrollLeft = Math.max(0, offset - (track.clientWidth - item.offsetWidth) / 2);
    }

    /* ---------------- "Visa fler" */

    async loadMore(link) {
      if (link.getAttribute('aria-busy') === 'true') return;
      const url = cleanURL(link.href);
      const JV = api();
      link.setAttribute('aria-busy', 'true');
      try {
        const request = new URL(url.toString());
        request.searchParams.set('section_id', this.sectionId);
        const response = await fetch(request.toString(), { headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(`Loading failed (${response.status})`);
        const doc = parseHTML(await response.text());
        sanitizeLinks(doc);
        const fresh = doc.querySelector(`jv-collection[data-section-id="${escapeSelector(this.sectionId)}"]`);
        if (!fresh) throw new Error('Loading failed (no results)');

        let first = null;
        ['grid', 'rows'].forEach((name) => {
          const target = this.querySelector(`[data-jv-append="${name}"]`);
          const incoming = fresh.querySelector(`[data-jv-append="${name}"]`);
          if (!target || !incoming) return;
          [...incoming.children].forEach((item, index) => {
            const node = document.importNode(item, true);
            node.style.setProperty('--jv-delay', `${(index % 4) * 80}ms`);
            target.appendChild(node);
            if (!first) first = node;
          });
          const wrap = target.closest('[data-jv-rows]');
          if (wrap && target.children.length) wrap.hidden = false;
        });

        const pager = this.region('pager');
        const nextPager = this.region('pager', fresh);
        if (pager && nextPager) pager.replaceWith(document.importNode(nextPager, true));
        if (typeof JV.reveal === 'function') JV.reveal(this);
        if (JV.wishlist && typeof JV.wishlist.sync === 'function') JV.wishlist.sync(this);
        const focusTarget = first ? first.querySelector('a[href], button:not([disabled])') : null;
        if (focusTarget) focusTarget.focus({ preventScroll: true });
        const items = this.querySelectorAll('[data-jv-append="grid"] > li').length;
        this.announce(this.dataset.loadedLabel ? this.dataset.loadedLabel.replace('[count]', items) : '');
      } catch (error) {
        window.location.assign(url.toString());
      } finally {
        link.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('jv-collection', JvCollection);
})();
